import React from 'react';
import { X, Calendar, MapPin, Hash, Users, IndianRupee, Filter } from 'lucide-react';
import ExportDropdown from './ExportDropdown';

const EventDetailsModal = ({ 
  event, 
  onClose, 
  loadingTransactions, 
  formatDate, 
  formatTime, 
  formatCurrency,
  PaymentStatusBadge,
  onDownloadEventReport,
  eventSpecificLoading
}) => {
  if (!event) return null;

  const filteredTransactions = (event.transactions || []);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl min-h-[50vh] max-h-[90vh] overflow-hidden">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{event.Event_Name} - Payment Report</h2>
              <div className="flex items-center gap-3 mt-2">
                <span className="text-sm text-gray-600 flex items-center">
                  <Calendar size={14} className="mr-1" />
                  {formatDate(event.Start_datetime)} • {formatTime(event.Start_datetime)}
                </span>
                <span className="text-sm text-gray-600 flex items-center">
                  <MapPin size={14} className="mr-1" />
                  {event.Event_Place}
                </span>
                <span className="text-sm text-gray-600 flex items-center">
                  <Hash size={14} className="mr-1" />
                  Event ID: {event.Event_Id}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>
          </div>
        </div>

        {/* Modal Body */}
        <div className="overflow-y-auto max-h-[calc(90vh-160px)]">
          <div className="p-6">
            {loadingTransactions ? (
              <div className="flex justify-center items-center py-20">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-4 text-gray-600">Loading payment details...</p>
                </div>
              </div>
            ) : (
              <>
                {/* Summary Cards */}
                {event.summary && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-5 rounded-xl border border-blue-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm text-blue-700 mb-2 flex items-center">
                            <Users size={16} className="mr-2" />
                            Total Registrations
                          </div>
                          <div className="text-2xl font-bold text-blue-900">{event.summary.Total_Registration}</div>
                          <div className="text-xs text-blue-600 mt-1">
                            All participant types
                          </div>
                        </div>
                        <div className="text-blue-200">
                          <Users size={32} />
                        </div>
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-green-50 to-green-100 p-5 rounded-xl border border-green-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm text-green-700 mb-2 flex items-center">
                            <IndianRupee size={16} className="mr-2" />
                            Total Collected
                          </div>
                          <div className="text-2xl font-bold text-green-900">
                            {event.summary.Total_Collected}
                          </div>
                        </div>
                        <div className="text-green-200">
                          <IndianRupee size={32} />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Payment Details Table */}
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div>
                        <h4 className="font-semibold text-gray-900">
                          Payment Details
                          <span className="text-sm font-normal text-gray-600 ml-2">
                            ({filteredTransactions.length} records)
                          </span>
                        </h4>
                        <p className="text-xs text-gray-500 mt-1">
                          Showing payment transactions for {event.Event_Name}
                        </p>
                      </div>
                      {filteredTransactions.length > 0 && (
                        <div className="flex items-center gap-3">
                          <ExportDropdown
                            eventId={event.Event_Id}
                            label="Export Payments"
                            variant="primary"
                            handleDownloadEventReport={onDownloadEventReport}
                            eventSpecificLoading={eventSpecificLoading}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Receipt Details
                          </th>
                          <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Participant
                          </th>
                          <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Amount Details
                          </th>
                          <th className="py-3 px-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {filteredTransactions.length > 0 ? (
                          filteredTransactions.map((transaction, index) => (
                            <tr key={transaction.TransactionId || index} className="hover:bg-gray-50">
                              <td className="py-3 px-4">
                                <div className="space-y-1">
                                  <div className="font-medium text-gray-900">{transaction.TransactionId}</div>
                                  <div className="text-sm text-gray-600">
                                    {transaction.TransactionDate ? formatDate(transaction.TransactionDate) : 'N/A'}
                                    <span className="ml-2 text-gray-500">
                                      {transaction.TransactionTime || formatTime(transaction.TransactionDate)}
                                    </span>
                                  </div>
                                  <div className="text-xs text-gray-400">
                                    Reg ID: {transaction.RegistrationId}
                                  </div>
                                </div>
                              </td>
                              <td className="py-3 px-4">
                                <div className="space-y-1">
                                  <div className="font-medium text-gray-900">{transaction.CustomerName}</div>
                                  <div className="text-sm text-gray-600">
                                    {transaction.ParticipantType}
                                  </div>
                                </div>
                              </td>
                              <td className="py-3 px-4">
                                <div className="space-y-1">
                                  <div className="text-lg font-bold text-green-600">
                                    {formatCurrency(transaction.Amount)}
                                  </div>
                                </div>
                              </td>
                              <td className="py-3 px-4">
                                <div className="space-y-2">
                                  <PaymentStatusBadge status={
                                    transaction.Status === 'successful' ? 1 :
                                    transaction.Status === 'pending' ? 2 :
                                    transaction.Status === 'refunded' ? 5 : 0
                                  } />
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="5" className="py-12 text-center text-gray-500">
                              <div className="flex flex-col items-center justify-center">
                                <Filter size={48} className="text-gray-300 mb-3" />
                                <div className="text-lg font-medium text-gray-400">No payments found</div>
                                <div className="text-sm text-gray-500 mt-1 mb-4">
                                  No payment records available for this event
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="text-sm text-gray-500">
              <div className="flex items-center gap-4">
                <span>
                  Showing {filteredTransactions.length} of {event.transactions?.length || 0} payments
                </span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                Close Report
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EventDetailsModal;